import React from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { cn } from '../../lib/utils';
import { Modal } from './Modal';

// Shared building blocks for the add / edit / view dialogs, so every form
// uses the same field, section and footer styling.

export const inputClass =
  'block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 read-only:bg-gray-50 read-only:text-gray-500';

export const inputErrorClass = 'border-red-400 focus:border-red-500 focus:ring-red-500';

export const primaryButtonClass =
  'inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

export const secondaryButtonClass =
  'inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

export const dangerButtonClass =
  'inline-flex items-center justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

export const badgeClass =
  'inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset';

export interface FieldProps {
  label: string;
  htmlFor?: string;
  required?: boolean;
  error?: string;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}

export const Field: React.FC<FieldProps> = ({
  label,
  htmlFor,
  required,
  error,
  hint,
  className,
  children,
}) => (
  <div className={className}>
    <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700">
      {label}
      {required && <span className="ml-0.5 text-red-500">*</span>}
    </label>
    <div className="mt-1">{children}</div>
    {error ? (
      <p className="mt-1 text-sm text-red-600">{error}</p>
    ) : hint ? (
      <p className="mt-1 text-xs text-gray-500">{hint}</p>
    ) : null}
  </div>
);

export interface FormSectionProps {
  title: string;
  description?: string;
  columns?: 2 | 3 | 4;
  children: React.ReactNode;
}

const columnClasses = {
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-2 lg:grid-cols-3',
  4: 'sm:grid-cols-2 lg:grid-cols-4',
};

export const FormSection: React.FC<FormSectionProps> = ({
  title,
  description,
  columns = 2,
  children,
}) => (
  <section>
    <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</h4>
    {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
    <div className={cn('mt-3 grid grid-cols-1 gap-x-4 gap-y-4', columnClasses[columns])}>
      {children}
    </div>
  </section>
);

export interface FormCardProps {
  title: string;
  description?: string;
  className?: string;
  children: React.ReactNode;
}

// A bordered group of related fields, laid out in two columns
export const FormCard: React.FC<FormCardProps> = ({ title, description, className, children }) => (
  <section className={cn('rounded-lg border border-gray-200 bg-white', className)}>
    <div className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3">
      <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      {description && <p className="mt-0.5 text-xs text-gray-500">{description}</p>}
    </div>
    <div className="grid grid-cols-1 gap-x-4 gap-y-4 p-4 sm:grid-cols-2">{children}</div>
  </section>
);

// Footer that spans the full width of a ui/Modal body
export const FormActions: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="-mx-6 -mb-4 mt-6 flex flex-col-reverse gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row sm:justify-end">
    {children}
  </div>
);

export interface DetailItemProps {
  label: string;
  children: React.ReactNode;
  className?: string;
}

export const DetailItem: React.FC<DetailItemProps> = ({ label, children, className }) => (
  <div className={className}>
    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
    <dd className="mt-1 text-sm font-medium text-gray-900">{children}</dd>
  </div>
);

export const DetailCard: React.FC<{
  title: string;
  columns?: 2 | 3 | 4;
  children: React.ReactNode;
}> = ({ title, columns = 2, children }) => (
  <section className="rounded-lg border border-gray-200 bg-white">
    <h4 className="border-b border-gray-200 bg-gray-50 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500 rounded-t-lg">
      {title}
    </h4>
    <dl className={cn('grid grid-cols-2 gap-x-4 gap-y-4 p-4', columnClasses[columns])}>{children}</dl>
  </section>
);

export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  pendingLabel?: string;
  isPending?: boolean;
  tone?: 'danger' | 'primary';
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  pendingLabel = 'Working...',
  isPending = false,
  tone = 'danger',
}) => (
  <Modal isOpen={isOpen} onClose={() => !isPending && onClose()} size="sm" showCloseButton={false}>
    <div className="flex gap-4 pt-2">
      <span
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
          tone === 'danger' ? 'bg-red-50 text-red-600' : 'bg-indigo-50 text-indigo-600',
        )}
      >
        <ExclamationTriangleIcon className="h-5 w-5" aria-hidden="true" />
      </span>
      <div>
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <div className="mt-1 text-sm text-gray-600">{message}</div>
      </div>
    </div>
    <FormActions>
      <button type="button" onClick={onClose} disabled={isPending} className={secondaryButtonClass}>
        Go Back
      </button>
      <button
        type="button"
        onClick={onConfirm}
        disabled={isPending}
        className={tone === 'danger' ? dangerButtonClass : primaryButtonClass}
      >
        {isPending ? pendingLabel : confirmLabel}
      </button>
    </FormActions>
  </Modal>
);

// A stock quantity: a number with at most four decimals (kg, metres, litres)
export const QUANTITY_STEP = '0.0001';
export const isQuantity = (value: number) => Number.isFinite(value) && Math.abs(Math.round(value * 10000) / 10000 - value) < 1e-9;

export const formatNumber = (value: number, maximumFractionDigits = 4) =>
  Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits });

export const formatMoney = (value: number) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

export const getApiErrorMessage = (error: any, fallback: string) => {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  return message || fallback;
};
